import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import React, { forwardRef } from "react";

export const Input = forwardRef(
    (
        {
            id,
            label,
            error,
            errorText,
            type = "",
            value = "",
            isPassword,
            showPassword,
            togglePasswordVisibility,
            handleSendOtp,
            OTP,
            otpButtonText = "Send OTP",
            otpButtonDisabled = false,
            ...props
        },
        ref
    ) => {
        return (
            <div className={`${props.className ? props.className : ""}`}>
                {label && (
                    <label
                        htmlFor={id}
                        className="mb-3 block text-sm font-medium text-gray-700"
                    >
                        {label}
                        {props.required && <span className="text-red-500"> *</span>}
                    </label>
                )}
                <div className="relative">
                    {type === "file" ? (
                        <>
                            <input
                                ref={ref}
                                id={id}
                                type={type}
                                {...props}
                                className="block w-full appearance-none rounded-md border border-gray-200 px-3 py-2 text-gray-900 placeholder-[#A0AEC0] focus:border-blue-500k focus:bg-white focus:outline-none focus:ring-blue-500 sm:text-sm"
                            />
                            {value && typeof value === "string" && (
                                <span className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                    {value}
                                </span>
                            )}
                        </>
                    ) : (
                        <div className="flex items-center border border-gray-300 rounded-md px-3 py-2">
                            <input
                                ref={ref}
                                id={id}
                                type={type}
                                value={value}
                                placeholder="123456"
                                {...props}
                                className="flex-grow appearance-none text-gray-500 placeholder-gray-400 focus:outline-none"
                            />
                            {OTP && <button
                                type="button"
                                className="text-blue-700 whitespace-nowrap hover:underline focus:outline-none disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
                                onClick={handleSendOtp}
                                disabled={otpButtonDisabled}
                            >
                                {otpButtonText}
                            </button>}
                        </div>
                    )}
                    {isPassword && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                className={`h-4 w-4 ${showPassword ? "text-blue-500" : "text-gray-500"
                                    }`}
                                onClick={togglePasswordVisibility}
                            >
                                {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                            </svg>
                        </div>
                    )}
                </div>
                <div className="text-red-500 text-xs">{error && errorText}</div>
            </div>
        );
    }
);

export const EditableField = React.memo(({ value, onChange, type = "text", style = {}, overAllFontSize = 18, disabled = false }) => {
    return (
        <input
            type={type}
            value={value || ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            style={{
                border: 'none',
                borderBottom: disabled ? "none" : `1px solid #000`,
                background: 'transparent',
                padding: '2px 0',
                margin: '0 4px',
                outline: 'none',
                fontFamily: ['Times New Roman', 'Arial', 'Courier New'],
                fontSize: `${overAllFontSize}px`,
                ...style
            }}
        />
    );
});

export const EditableFieldNumber = React.memo(({
    value,
    onChange,
    type = "number",
    style = {},
    overAllFontSize = 18,
    disabled = false
}) => {

    const handleChange = (e) => {
        const inputValue = e.target.value;

        // Accept only positive integers (no 0, no negatives, no empty)
        if (/^[1-9]\d*$/.test(inputValue)) {
            onChange(inputValue);
        }
    };

    return (

        <>
            {
                <input
                    type={type}
                    value={value}
                    disabled={value?.length == 0 || disabled}
                    onChange={handleChange}
                    style={{
                        border: 'none',
                        borderBottom: disabled ? "none" : `1px solid #000`,
                        background: 'transparent',

                        padding: '2px 0',
                        margin: '0 4px',
                        outline: 'none',
                        fontFamily: ['Times New Roman', 'Arial', 'Courier New'],
                        fontSize: `${overAllFontSize}px`,
                        ...style
                    }}
                />
            }
        </>
    );
});


export function TextArea({ id, label, type = "text", ...props }) {
    return (
        <div className={`${props.className ? props.className : ""}`}>
            {label && (
                <label
                    htmlFor={id}
                    className="mb-3 block text-sm font-medium text-gray-700"
                >
                    {label}
                    {props.required && <span className="text-red-400"> *</span>}
                </label>
            )}
            <textarea
                id={id}
                type={type}
                placeholder={"Type here..."}
                {...props}
                className="block w-full appearance-none rounded-md border border-gray-200 px-3 py-4 text-gray-900 placeholder-gray-400 focus:border-blue-500k focus:bg-white focus:outline-none focus:ring-blue-500 sm:text-sm"
            />
        </div>
    );
}

